import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiAlertTriangle, FiX, FiHome, FiCheck,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE    = 10;
const getToken     = () => localStorage.getItem('toolboxToken') || '';
const getAmsToken  = () => localStorage.getItem('amsToken')     || '';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
};

const CONSTRUCTION_TYPES = [
  'Frame','Masonry','Masonry Veneer','Superior','Log','Metal',
  'Manufactured Housing','Modular','Other',
];

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface HistoryRow {
  transactionId: string;
  userName: string;
  status: string;
  isUpdateInAMS: boolean;
  isLiked: boolean | null;
  createDate: string;
  totalCount: number;
  responseJson: string;
  submittedJsonData: string;
  selectionOption: string;
}

interface FenrisFields {
  constructionType?: string;
  occupancy?: string;
  residenceType?: string;
  usageType?: string;
  yearBuilt?: string;
  room?: string;
  families?: string;
  marketValue?: string;
  replacementCost?: string;
  totalLivingArea?: string;
  roofMaterial?: string;
  primaryHeat?: string;
}

interface SelectionOption {
  clientName?: string;
  insuredDatabaseId?: string;
  buildingId?: string;
}

const FenrisPersonalLineHistory: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [rows, setRows]             = useState<HistoryRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview modal */
  const [previewRow, setPreviewRow]       = useState<HistoryRow | null>(null);
  const [previewFields, setPreviewFields] = useState<FenrisFields>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [prevInsuredId, setPrevInsuredId] = useState('');
  const [prevBuildingId, setPrevBuildingId] = useState('');

  /* Feedback popup */
  const [feedbackRowId,      setFeedbackRowId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedbackError,      setFeedbackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Fetch ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisPersonalLine/history?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: HistoryRow[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.userName?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const openPreview = useCallback((row: HistoryRow) => {
    setPreviewRow(row);
    setSubmitSuccess(false);
    setPrevInsuredId('');
    setPrevBuildingId('');

    try {
      const selectionOpt: SelectionOption = row.selectionOption ? JSON.parse(row.selectionOption) : {};
      setPrevInsuredId(selectionOpt.insuredDatabaseId || '');
      setPrevBuildingId(selectionOpt.buildingId || '');

      if (row.isUpdateInAMS) {
        const submitted: FenrisFields = row.submittedJsonData ? JSON.parse(row.submittedJsonData) : {};
        setPreviewFields({
          constructionType: submitted.constructionType || '',
          occupancy:        submitted.occupancy        || '',
          residenceType:    submitted.residenceType    || '',
          usageType:        submitted.usageType        || '',
          yearBuilt:        submitted.yearBuilt        || '',
          room:             submitted.room             || '',
          families:         submitted.families         || '',
          marketValue:      submitted.marketValue      || '',
          replacementCost:  submitted.replacementCost  || '',
          totalLivingArea:  submitted.totalLivingArea  || '',
          roofMaterial:     submitted.roofMaterial     || '',
          primaryHeat:      submitted.primaryHeat      || '',
        });
      } else {
        const rj = row.responseJson ? JSON.parse(row.responseJson) : {};
        const bc = rj?.document?.buildingCharacteristics ?? {};
        const val = rj?.document?.valuation ?? {};
        setPreviewFields({
          constructionType: bc.construction   ?? '',
          occupancy:        bc.ownerOccupied  ?? '',
          yearBuilt:        bc.yearBuilt      ?? '',
          roofMaterial:     bc.roofCovering   ?? '',
          room:             bc.totalRooms     ?? '',
          totalLivingArea:  bc.totalArea      ?? '',
          marketValue:      val.assessedMarketValue ?? '',
          primaryHeat:      bc.heatingType    ?? '',
          usageType:        bc.useType        ?? '',
          residenceType:    '',
          families:         '',
          replacementCost:  '',
        });
      }
    } catch { /* malformed JSON — leave fields empty */ }
  }, []);

  /* ── Re-submit from history ── */
  const onPreviewSubmit = useCallback(async () => {
    if (!previewRow) return;
    setSubmitLoading(true);
    try {
      const ncToken = getAmsToken();
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addaccordeighty`,
        {
          databaseId:                                      prevInsuredId,
          buildingId:                                      prevBuildingId,
          insuredDatabaseId:                               prevInsuredId,
          constructionTypeOtherIndicator:                  true,
          constructionTypeOtherCode:                       previewFields.constructionType,
          sidingOtherUsageIndicator:                       true,
          sidingOtherUsageDescription:                     previewFields.usageType,
          otherOccupancyIndicator:                         true,
          otherOccupancyDescription:                       previewFields.occupancy,
          constructionRoofMaterialCode:                    previewFields.roofMaterial,
          constructionBuiltYear:                           previewFields.yearBuilt ? String(previewFields.yearBuilt) : '',
          marketValueAmount:                               previewFields.marketValue ? String(previewFields.marketValue) : '',
          totalLivingArea:                                 previewFields.totalLivingArea ? String(previewFields.totalLivingArea) : '',
          roomCount:                                       previewFields.room ? String(previewFields.room) : '',
          heatingUnitPrimaryHeatSource:                    previewFields.primaryHeat,
          residentialStructureFireplaceChimneyCount:       '0',
          residentialStructureFireplaceHearthCount:        '0',
          residentialStructureFireplacePrefabricatedCount: '0',
          residentialStructureFireplaceWoodStoveInsertCount: '0',
        },
        { params: { ncToken }, headers: { Authorization: `Bearer ${getToken()}` } },
      );
      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/fenrispersonalline/update`,
        { submittedJsonData: JSON.stringify(previewFields), transactionId: previewRow.transactionId },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setSubmitSuccess(true);
      showSuccess('Submitted to NowCerts successfully');
      fetchData(page);
    } catch {
      showError('Failed to submit to NowCerts');
    } finally {
      setSubmitLoading(false);
    }
  }, [previewRow, previewFields, prevInsuredId, prevBuildingId, page, fetchData]);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedbackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 5, TransactionId: id, IsLiked: isLiked, Feedback: '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully.')).catch(() => {});
  }, []);

  const onThumbsUp = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onThumbsDown = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Max 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 5, TransactionId: feedbackRowId, IsLiked: cur?.isLiked ?? feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); showError('Failed to submit feedback'); });
  }, [feedbackRowId, feedbackText, feedbackRating, rows, closeFeedback]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>Fenris Personal Line History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="cs-client-search__input-wrap" style={{ minWidth: '220px' }}>
              <FiSearch size={13} className="cs-client-search__icon" />
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiClock size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiHome size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching Fenris personal line records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiHome size={28} />
              <p>{search ? 'No records match your search.' : 'No Fenris personal line lookups yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Status</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Updated in AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.transactionId ?? i} className="qp-table__row">
                      <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.createDate)}</td>
                      <td className="qp-table__td">{row.userName || '—'}</td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[row.status] || STATUS_CONFIG.Failed).color }}>
                          {(STATUS_CONFIG[row.status] || { label: row.status }).label}
                        </span>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: row.isUpdateInAMS ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.1)',
                          color:      row.isUpdateInAMS ? '#34d399'               : '#f87171',
                        }}>
                          {row.isUpdateInAMS ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button
                          className="qp-table__action-btn"
                          title="Preview"
                          onClick={() => openPreview(row)}
                        >
                          <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                        </button>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button
                          className="qp-table__action-btn"
                          title="Feedback"
                          onClick={e => openFeedback(row, e)}
                          style={{ margin: '0 auto' }}
                        >
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback popup (portalled) ── */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          {feedbackError && (
            <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>
              {feedbackError}
            </span>
          )}
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUp}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDown}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedback}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview / Re-submit modal (portalled) ── */}
      {previewRow && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'var(--modal-overlay, rgba(0,0,0,0.75))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPreviewRow(null)}
        >
          <div className="ho-modal" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <FiHome size={16} className="ho-modal__icon" />
                <h3 className="ho-modal__title">Fenris Property Details</h3>
                {(previewRow.isUpdateInAMS || submitSuccess) && (
                  <span className="ho-badge ho-badge--success"><FiCheck size={10} /> In AMS</span>
                )}
              </div>
              <button className="ho-modal__close" onClick={() => setPreviewRow(null)}>
                <FiX size={16} />
              </button>
            </div>

            {/* Fields */}
            <div className="ho-modal__body">
              {Object.keys(previewFields).length === 0 ? (
                <div className="qp-empty-state" style={{ padding: '32px 0' }}>
                  <FiAlertTriangle size={22} />
                  <p>No property data available for this record.</p>
                </div>
              ) : (
                <div className="ho-form-grid">
                  <div className="ho-form-field">
                    <label className="ho-form-label">Construction Type</label>
                    <div className="ho-select-wrap">
                      <select
                        className="ho-select"
                        value={previewFields.constructionType || ''}
                        onChange={e => setPreviewFields(p => ({ ...p, constructionType: e.target.value }))}
                        disabled={previewRow.isUpdateInAMS || submitSuccess}
                      >
                        <option value="">Select Construction Type</option>
                        {CONSTRUCTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  {([
                    ['occupancy',       'Occupancy'],
                    ['residenceType',   'Residence Type'],
                    ['usageType',       'Usage Type'],
                    ['yearBuilt',       'Year Built'],
                    ['room',            'Rooms'],
                    ['families',        'Families'],
                    ['marketValue',     'Market Value ($)'],
                    ['replacementCost', 'Replacement Cost ($)'],
                    ['totalLivingArea', 'Total Living Area'],
                    ['roofMaterial',    'Roof Material'],
                    ['primaryHeat',     'Primary Heat'],
                  ] as [keyof FenrisFields, string][]).map(([key, label]) => (
                    <div className="ho-form-field" key={key}>
                      <label className="ho-form-label">{label}</label>
                      <input
                        className="ho-input"
                        value={previewFields[key] || ''}
                        placeholder={`Enter ${label}`}
                        onChange={e => setPreviewFields(p => ({ ...p, [key]: e.target.value }))}
                        disabled={previewRow.isUpdateInAMS || submitSuccess}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="ho-modal__footer" style={{ justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPreviewRow(null)}
                style={{
                  padding: '8px 20px', fontSize: 13, fontWeight: 600,
                  background: 'var(--surface-glass)', color: 'var(--text-secondary)',
                  border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer',
                }}
              >
                Close
              </button>
              {!previewRow.isUpdateInAMS && !submitSuccess && (
                <motion.button
                  className="ho-search-btn"
                  onClick={onPreviewSubmit}
                  disabled={submitLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitLoading ? 'Submitting…' : 'Submit to NowCerts'}
                </motion.button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default FenrisPersonalLineHistory;

